import React, { useEffect, useRef, useState } from 'react';
import { Alert, Empty, InputNumber, Modal, Select, Space, message } from 'antd';
import { ConsignmentAddStockBulkPayload, ConsignmentAddStockBulkResult, ConsignmentItem } from '../../model/consignment';
import { api_product } from '../../api/product';

const genIdempotencyKey = (): string => {
    // crypto.randomUUID is available in all modern browsers; fall back for
    // older test/JSDOM environments that don't implement it.
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `add-stock-bulk-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

/** How the modal was opened.
 *
 * AFTER_CLOSE_PERIOD -- straight after CLOSE_PERIOD: the Carry Forward
 * rows are shown and more of those Products, or other Products, can be
 * deposited.
 *
 * ADD_NEW_PRODUCTS -- later, from the bill's current-Period banner: a
 * Product the shop has only just been given to sell, added to the Period
 * that is open now. Same ADD_STOCK_BULK save, so every line is its own
 * new ADD_STOCK lot. */
export type NewPeriodAddStockMode = 'AFTER_CLOSE_PERIOD' | 'ADD_NEW_PRODUCTS';

/** Everything the modal needs to know about the Period it adds to.
 * `newPeriod.id` becomes `expected_period_id` on save; it must be the
 * Period the caller just observed as OPEN, never assumed/re-derived later. */
export interface NewPeriodAddStockContext {
    consignmentId: number;
    documentNo: string;
    vendorName?: string;
    newPeriod: { id: number; period_no: number };
    /** CARRY_FORWARD Batches already persisted into `newPeriod` by
     * CLOSE_PERIOD -- read-only context, never resubmitted. */
    carryForwardItems: ConsignmentItem[];
    /** Defaults to AFTER_CLOSE_PERIOD. */
    mode?: NewPeriodAddStockMode;
    /** Products with an ACTIVE lot in this Period already. They are not
     * offered here: their own "+ ฝากเพิ่ม" is how more of them come in. */
    activeProductIds?: number[];
}

interface Props {
    open: boolean;
    context: NewPeriodAddStockContext | null;
    onClose: () => void;
    onSaveBulk: (payload: ConsignmentAddStockBulkPayload) => Promise<ConsignmentAddStockBulkResult>;
    /** The Period changed under this form: the caller reloads the bill. */
    onStale?: () => void;
}

interface BulkRow {
    product_id: number;
    product_name?: string;
    product_code?: string;
    product_unit_name?: string;
    /** Sum of Carry Forward quantity already in the new Period for this
     * Product -- display only, never part of the submitted add_quantity. */
    carryTotal: number;
    carryBatches: ConsignmentItem[];
    /** undefined = no new deposit for this Product this time (never
     * submitted as a line, never treated as 0). */
    addQuantity: number | undefined;
    sellingPrice: number | undefined;
    ownerCost: number | undefined;
    /** true = picked from the Product search (no Carry Forward row backing it). */
    isExtra: boolean;
}

/** The product search asks the server a page at a time: the catalogue is
 * over 2,600 products and LIST never returns more than 200, so the old
 * "load 500 and filter here" could not reach most of it. */
const PRODUCT_SEARCH_PAGE_SIZE = 20;
const PRODUCT_SEARCH_DEBOUNCE_MS = 300;

/** A price from the product card, or empty for the shop to fill in.
 *
 * The card stores "not filled in" as 0, never NULL (Production 2026-09-24:
 * 1,383 of 2,624 products have cost 0, 267 have price 0), and no
 * consignment lot on Staging or Production has ever carried a 0 price or
 * payout. So 0 leaves the box empty -- the same rule as the new-bill form. */
export const masterPrice = (value: any): number | undefined => {
    const n = Number(value);
    return value !== null && value !== undefined && value !== '' && Number.isFinite(n) && n > 0 ? n : undefined;
};

const buildRowsFromCarryForward = (items: ConsignmentItem[]): BulkRow[] => {
    const byProduct = new Map<number, ConsignmentItem[]>();
    items.forEach(it => {
        const list = byProduct.get(it.product_id) || [];
        list.push(it);
        byProduct.set(it.product_id, list);
    });
    return Array.from(byProduct.entries()).map(([product_id, batches]) => {
        const first = batches[0];
        return {
            product_id,
            product_name: first?.product_name,
            product_code: first?.product_code,
            product_unit_name: first?.product_unit_name,
            carryTotal: batches.reduce((sum, b) => sum + Number(b.quantity_received || 0), 0),
            carryBatches: batches,
            addQuantity: undefined,
            sellingPrice: first ? Number(first.selling_price_per_unit || 0) : undefined,
            ownerCost: first ? Number(first.owner_cost_per_unit || 0) : undefined,
            isExtra: false
        };
    });
};

const isMissingMoney = (value: number | undefined) =>
    value === undefined || value === null || !Number.isFinite(Number(value)) || Number(value) < 0;

export const ConsignmentNewPeriodAddStockModal: React.FC<Props> = ({ open, context, onClose, onSaveBulk, onStale }) => {
    const [rows, setRows] = useState<BulkRow[]>([]);
    const [products, setProducts] = useState<any[]>([]);
    const [searching, setSearching] = useState(false);
    const [searchFailed, setSearchFailed] = useState(false);
    const [extraProductId, setExtraProductId] = useState<number | undefined>(undefined);
    const [idempotencyKey, setIdempotencyKey] = useState('');
    const [saving, setSaving] = useState(false);
    const searchSeq = useRef(0);
    const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const mode: NewPeriodAddStockMode = context?.mode || 'AFTER_CLOSE_PERIOD';
    const addingNewProducts = mode === 'ADD_NEW_PRODUCTS';

    // Only the newest search may fill the list: a slow early answer must
    // not overwrite what the user typed since.
    const runSearch = (text: string) => {
        const seq = ++searchSeq.current;
        setSearching(true);
        setSearchFailed(false);
        api_product('LIST', { search: text.trim() || undefined, page: 1, page_size: PRODUCT_SEARCH_PAGE_SIZE })
            .then(res => {
                if (seq === searchSeq.current) setProducts(Array.isArray(res) ? res : (res?.items || []));
            })
            .catch(() => {
                if (seq !== searchSeq.current) return;
                setProducts([]);
                setSearchFailed(true);
            })
            .finally(() => {
                if (seq === searchSeq.current) setSearching(false);
            });
    };

    const handleSearch = (text: string) => {
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => runSearch(text), PRODUCT_SEARCH_DEBOUNCE_MS);
    };

    useEffect(() => () => {
        if (searchTimer.current) clearTimeout(searchTimer.current);
    }, []);

    useEffect(() => {
        if (!open || !context) return;
        setRows(buildRowsFromCarryForward(context.carryForwardItems));
        setExtraProductId(undefined);
        setIdempotencyKey(genIdempotencyKey());
        runSearch('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, context?.consignmentId, context?.newPeriod?.id, context?.mode]);

    if (!context) return null;

    const activeProductIds = new Set(context.activeProductIds || []);

    const handleQuantityChange = (productId: number, value: number | undefined) => {
        setRows(prev => prev.map(r => r.product_id === productId ? { ...r, addQuantity: value } : r));
    };
    const handlePriceChange = (productId: number, field: 'sellingPrice' | 'ownerCost', value: number | undefined) => {
        setRows(prev => prev.map(r => r.product_id === productId ? { ...r, [field]: value } : r));
    };

    const handleAddExtraProduct = (productId: number) => {
        setExtraProductId(undefined);
        if (rows.some(r => r.product_id === productId)) {
            message.warning('สินค้านี้อยู่ในรายการอยู่แล้ว กรุณากรอกจำนวนในแถวเดิม');
            return;
        }
        if (activeProductIds.has(productId)) {
            message.warning('สินค้านี้มีล็อตที่ใช้งานอยู่ในรอบนี้แล้ว กรุณาใช้ปุ่ม "+ ฝากเพิ่ม" ของสินค้านั้นแทน');
            return;
        }
        const prod = products.find(p => p.id === productId);
        setRows(prev => ([
            ...prev,
            {
                product_id: productId,
                product_name: prod?.product_name,
                product_code: prod?.product_code,
                product_unit_name: prod?.unit_name || prod?.product_unit_name || 'ชิ้น',
                carryTotal: 0,
                carryBatches: [],
                addQuantity: undefined,
                sellingPrice: masterPrice(prod?.price_retail),
                ownerCost: masterPrice(prod?.product_cost),
                isExtra: true
            }
        ]));
    };

    const handleRemoveExtraRow = (productId: number) => {
        setRows(prev => prev.filter(r => !(r.isExtra && r.product_id === productId)));
    };

    const handleSaveAll = async () => {
        // A Product the user picked on purpose must not be dropped quietly.
        const pickedWithoutQuantity = rows.find(r => r.isExtra && !(Number(r.addQuantity) > 0));
        if (pickedWithoutQuantity) {
            message.error(`กรุณากรอกจำนวนฝากของ "${pickedWithoutQuantity.product_name}" หรือเอาแถวนั้นออก`);
            return;
        }
        const items = rows.filter(r => r.addQuantity !== undefined && r.addQuantity !== null && r.addQuantity > 0);
        if (items.length === 0) {
            message.info(addingNewProducts
                ? 'ยังไม่ได้เลือกสินค้าที่จะเพิ่มในรอบนี้'
                : 'ยังไม่ได้ระบุจำนวนฝากเพิ่มสำหรับสินค้าใด ปิดหน้าต่างนี้ได้เลย (รอบบิลปิดสำเร็จแล้ว)');
            return;
        }
        for (const r of items) {
            if (!Number.isInteger(r.addQuantity)) {
                message.error(`จำนวนฝากเพิ่มของ "${r.product_name}" ต้องเป็นจำนวนเต็ม (ไม่รองรับทศนิยม)`);
                return;
            }
            // Never sent as 0: an empty price is a question for the shop,
            // not a number to guess.
            if (isMissingMoney(r.sellingPrice)) {
                message.error(`กรุณาระบุราคาขายต่อหน่วยของ "${r.product_name}"`);
                return;
            }
            if (isMissingMoney(r.ownerCost)) {
                message.error(`กรุณาระบุยอดจ่ายผู้ฝากต่อหน่วยของ "${r.product_name}"`);
                return;
            }
        }

        setSaving(true);
        try {
            await onSaveBulk({
                expected_period_id: context.newPeriod.id,
                idempotency_key: idempotencyKey,
                items: items.map(r => ({
                    product_id: r.product_id,
                    add_quantity: Number(r.addQuantity),
                    selling_price_per_unit: Number(r.sellingPrice),
                    owner_cost_per_unit: Number(r.ownerCost)
                }))
            });
            message.success(addingNewProducts
                ? `เพิ่มสินค้าใหม่ในรอบบิลที่ ${context.newPeriod.period_no} สำเร็จ`
                : 'ฝากเพิ่มรอบใหม่สำเร็จ');
            onClose();
        } catch (error: any) {
            const msg: string = error?.message || 'บันทึกฝากเพิ่มรอบใหม่ไม่สำเร็จ';
            if (msg.includes('STALE_PERIOD') || msg.includes('ALREADY_CLOSED')) {
                message.error('รอบบิลนี้มีการเปลี่ยนแปลงแล้ว กรุณารีเฟรชข้อมูลก่อนดำเนินการอีกครั้ง');
                onClose();
                onStale?.();
            } else {
                message.error(msg);
            }
        } finally {
            setSaving(false);
        }
    };

    const productOptions = products
        .filter(p => !rows.some(r => r.product_id === p.id))
        .map(p => {
            const alreadyActive = activeProductIds.has(p.id);
            return {
                value: p.id,
                disabled: alreadyActive,
                label: `[${p.product_code || '-'}] ${p.product_name}`
                    + (alreadyActive ? ' · มีล็อตในรอบนี้แล้ว ใช้ "+ ฝากเพิ่ม" ของสินค้านั้น' : '')
            };
        });

    return (
        <Modal
            className="consignment-new-period-modal consignment-add-stock-modal"
            open={open}
            title={addingNewProducts
                ? `➕ เพิ่มสินค้าใหม่ในรอบบิลที่ ${context.newPeriod.period_no}`
                : `📦 ฝากเพิ่มรอบใหม่ — รอบบิลที่ ${context.newPeriod.period_no}`}
            okText="บันทึกทั้งหมด"
            cancelText={addingNewProducts ? 'ยกเลิก' : 'ข้าม / ยังไม่ฝากเพิ่ม'}
            confirmLoading={saving}
            onOk={handleSaveAll}
            onCancel={saving ? undefined : onClose}
            okButtonProps={{ disabled: saving, 'data-testid': 'btn-save-new-period-bulk' } as any}
            cancelButtonProps={{ disabled: saving, 'data-testid': 'btn-skip-new-period-bulk' } as any}
            destroyOnClose
            width={720}
        >
            <Space direction="vertical" size={14} style={{ width: '100%' }}>
                {addingNewProducts ? (
                    <Alert
                        type="info"
                        showIcon
                        message={`เพิ่มสินค้าที่เพิ่งนำมาฝากเข้ารอบบิลที่ ${context.newPeriod.period_no} ของ ${context.documentNo} ${context.vendorName ? `(${context.vendorName})` : ''}`}
                        description={'สินค้าแต่ละรายการจะเป็นล็อตใหม่ (ฝากเพิ่ม) ในรอบนี้ สินค้าที่มีล็อตในรอบนี้อยู่แล้วให้ใช้ปุ่ม "+ ฝากเพิ่ม" ของสินค้านั้น'}
                    />
                ) : (
                    <Alert
                        type="info"
                        showIcon
                        message={`รอบบิลที่ ${context.newPeriod.period_no} ของ ${context.documentNo} ${context.vendorName ? `(${context.vendorName})` : ''} เปิดแล้ว`}
                        description="สต๊อกที่ยกมาจากรอบก่อนแสดงไว้ด้านล่างเพื่อการอ้างอิงเท่านั้น ไม่ต้องกรอกซ้ำ กรอกเฉพาะจำนวนที่ฝากเพิ่มใหม่จริงในรอบนี้"
                    />
                )}

                {rows.length === 0 && (
                    <Empty description={addingNewProducts ? 'ค้นหาแล้วเลือกสินค้าจากช่องด้านล่าง' : 'ไม่มีสินค้ายกมาจากรอบก่อน'} />
                )}

                {rows.map(row => (
                    <div className="consignment-bulk-row" key={row.product_id}>
                        <div className="consignment-bulk-row-header">
                            <div>
                                <div className="consignment-bulk-row-name">{row.product_name || `สินค้า #${row.product_id}`}</div>
                                <div className="consignment-bulk-row-code">รหัส: {row.product_code || '-'}</div>
                            </div>
                            {row.isExtra && (
                                <button
                                    type="button"
                                    className="consignment-bulk-row-remove"
                                    onClick={() => handleRemoveExtraRow(row.product_id)}
                                    data-testid={`btn-remove-extra-${row.product_id}`}
                                    aria-label="เอาสินค้านี้ออก"
                                >
                                    ✕
                                </button>
                            )}
                        </div>

                        {!row.isExtra && (
                            <div className="consignment-bulk-row-carry" data-testid={`carry-${row.product_id}`}>
                                ยกมาจากรอบก่อน: <strong>{row.carryTotal}</strong> {row.product_unit_name || 'ชิ้น'}
                                {row.carryBatches.length > 1 && (
                                    <span className="consignment-bulk-row-carry-detail">
                                        {' '}({row.carryBatches.map(b => `#${b.batch_no} ${b.quantity_received}@${Number(b.owner_cost_per_unit).toFixed(2)}`).join(', ')})
                                    </span>
                                )}
                            </div>
                        )}

                        <div className="consignment-bulk-row-fields">
                            <label>
                                <span className="consignment-add-stock-field-label">{row.isExtra ? 'จำนวนรับฝาก' : 'ฝากเพิ่มรอบนี้'}</span>
                                <InputNumber
                                    precision={0}
                                    placeholder="0"
                                    value={row.addQuantity}
                                    onChange={value => handleQuantityChange(row.product_id, value === null ? undefined : Number(value))}
                                    style={{ width: '100%' }}
                                    addonAfter={row.product_unit_name || 'ชิ้น'}
                                    data-testid={`input-bulk-qty-${row.product_id}`}
                                />
                            </label>
                            <label>
                                <span className="consignment-add-stock-field-label">ราคาขายต่อหน่วย</span>
                                <InputNumber
                                    min={0}
                                    precision={2}
                                    placeholder="กรอกราคาขาย"
                                    value={row.sellingPrice}
                                    onChange={value => handlePriceChange(row.product_id, 'sellingPrice', value === null ? undefined : Number(value))}
                                    addonBefore="฿"
                                    style={{ width: '100%' }}
                                    data-testid={`input-bulk-price-${row.product_id}`}
                                />
                            </label>
                            <label>
                                <span className="consignment-add-stock-field-label">ต้นทุนผู้ฝากต่อหน่วย</span>
                                <InputNumber
                                    min={0}
                                    precision={2}
                                    placeholder="กรอกยอดจ่ายผู้ฝาก"
                                    value={row.ownerCost}
                                    onChange={value => handlePriceChange(row.product_id, 'ownerCost', value === null ? undefined : Number(value))}
                                    addonBefore="฿"
                                    style={{ width: '100%' }}
                                    data-testid={`input-bulk-cost-${row.product_id}`}
                                />
                            </label>
                        </div>
                    </div>
                ))}

                <div className="consignment-bulk-add-extra">
                    <Select
                        showSearch
                        allowClear
                        placeholder={addingNewProducts ? '🔍 ค้นหาบาร์โค้ด / รหัส / ชื่อสินค้า เพื่อเพิ่ม' : '+ เพิ่มสินค้าอื่น'}
                        filterOption={false}
                        onSearch={handleSearch}
                        loading={searching}
                        notFoundContent={searching ? 'กำลังค้นหา...' : searchFailed
                            ? 'ค้นหาสินค้าไม่สำเร็จ กรุณาลองพิมพ์ค้นหาอีกครั้ง'
                            : 'ไม่พบสินค้า'}
                        value={extraProductId}
                        options={productOptions}
                        onChange={(value) => value !== undefined && value !== null && handleAddExtraProduct(Number(value))}
                        style={{ width: '100%' }}
                        data-testid="select-add-extra-product"
                    />
                    <div className="consignment-bulk-add-extra-hint">ค้นหาได้ทั้งแคตตาล็อก ด้วยบาร์โค้ด รหัส หรือชื่อสินค้า</div>
                </div>
            </Space>
        </Modal>
    );
};

export default ConsignmentNewPeriodAddStockModal;
